import React, { useEffect, useState } from "react";
import "./AudiobookFlipBook.css";

const PAPER = "#FFFFFF";
const COVER_EDGE = "#4569D4";

const SPREAD_FRAME_STYLE = {
  inset: "0",
  background: `linear-gradient(165deg, ${COVER_EDGE} 0%, #3d5fc4 50%, #3658b8 100%)`,
  borderRadius: "10px",
};

// Full-bleed cover: no padding/min-height — the cover art (which declares its
// own width + 3:4 aspect in AudiobookSinglePage) IS the book face; the shell
// just contributes the rounded corners, clipping, and drop shadow.
const coverPageShell = {
  background: PAPER,
  // Flatter left corners: that's the spine edge of the hardcover
  borderRadius: "5px 14px 14px 5px",
  overflow: "hidden",
  boxShadow: "0 20px 56px rgba(35,49,78,0.14)",
};

const PageStackBlock = () => (
  <div className="storybook-page-block" aria-hidden="true">
    <div className="storybook-stack storybook-stack--left" />
    <div className="storybook-stack storybook-stack--right" />
  </div>
);

const HardcoverFrame = ({ fading = false }) => (
  <div className={`storybook-hardcover ${fading ? "storybook-hardcover--fading" : ""}`} aria-hidden="true">
    <div className="storybook-cover-frame" style={SPREAD_FRAME_STYLE} />
  </div>
);

const PageArrow = ({ direction, onClick, disabled, isPlaying, extraClass = "" }) => {
  const isLeft = direction === "left";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || isPlaying}
      className={`storybook-nav-btn ${isLeft ? "storybook-nav-btn--left" : "storybook-nav-btn--right"} ${extraClass}`}
      aria-label={isLeft ? "Previous page" : "Next page"}
    >
      <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
        {isLeft ? (
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        ) : (
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        )}
      </svg>
    </button>
  );
};

const AudiobookFlipBook = ({
  pages,
  currentPageIndex,
  onPageChange,
  isPlaying,
  renderPage,
  onLayoutModeChange,
}) => {
  const [isFlipping, setIsFlipping] = useState(false);
  const [flipDirection, setFlipDirection] = useState("forward");
  const [targetPageIndex, setTargetPageIndex] = useState(null);
  const [isDesktopViewport, setIsDesktopViewport] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 1200 : true
  );

  useEffect(() => {
    const updateViewportMode = () => {
      setIsDesktopViewport(window.innerWidth >= 1200);
    };

    updateViewportMode();
    window.addEventListener("resize", updateViewportMode);
    return () => window.removeEventListener("resize", updateViewportMode);
  }, []);

  if (!pages?.length) return null;

  const currentPage = pages[currentPageIndex];
  if (!currentPage) return null;

  const nextPage = targetPageIndex !== null ? pages[targetPageIndex] : null;
  const hasCoverPage = pages[0]?.type === "cover";

  const hasEndPage = pages[pages.length - 1]?.type === "end";

  const getPageNumber = (index) => (hasCoverPage ? index : index + 1);
  const getTotalPages = () =>
    (hasCoverPage ? pages.length - 1 : pages.length) - (hasEndPage ? 1 : 0);

  const displayPageNumber = getPageNumber(currentPageIndex);
  const displayTotalPages = getTotalPages();
  // The synthetic end page renders as a single centered sheet like the
  // cover — a celebration spread split across two half-pages looks broken.
  const isCoverPage = currentPage?.type === "cover" || currentPage?.type === "end";
  const isTargetCoverPage = nextPage?.type === "cover" || nextPage?.type === "end";
  const useSpreadLayout = !isCoverPage || (isFlipping && nextPage && !isTargetCoverPage);

  const handlePageNavigation = (newIndex, direction) => {
    if (newIndex < 0 || newIndex >= pages.length || isPlaying || isFlipping) return;
    if (direction === "backward" && pages[newIndex]?.type === "cover") {
      onPageChange(newIndex);
      onLayoutModeChange?.(false);
      return;
    }
    if (direction === "forward" && isCoverPage) {
      onLayoutModeChange?.(true);
    }
    setFlipDirection(direction);
    setTargetPageIndex(newIndex);
    setIsFlipping(true);
    setTimeout(() => {
      onPageChange(newIndex);
      if (pages[newIndex]?.type === "cover") {
        onLayoutModeChange?.(false);
      }
      setIsFlipping(false);
      setTargetPageIndex(null);
    }, 800);
  };

  const handleMobileNavigation = (newIndex) => {
    if (newIndex < 0 || newIndex >= pages.length || isPlaying) return;
    onPageChange(newIndex);
  };

  const renderSpread = (leftContent, rightContent, flipClasses = {}) => (
    <div className={`storybook-spread-outer ${flipClasses.spread || ""}`}>
      <PageStackBlock />
      <div className="storybook-spread">
        <div className={`storybook-spread-page storybook-spread-page--left ${flipClasses.left || ""}`}>
          <div className="storybook-page-face storybook-page-face--left">{leftContent}</div>
        </div>
        <div className="storybook-spine-wrap">
          <div className="storybook-spine-shadow storybook-spine-shadow--left" />
          <div className="storybook-spine-shadow storybook-spine-shadow--right" />
          <div className="storybook-spine-ridge" />
          <div className="storybook-spine-bar" />
          <div className="storybook-gutter" />
        </div>
        <div className={`storybook-spread-page storybook-spread-page--right ${flipClasses.right || ""}`}>
          <div className="storybook-page-face storybook-page-face--right">{rightContent}</div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="storybook-root" style={{ userSelect: "none" }}>
      {/* MOBILE */}
      {!isDesktopViewport && (
      <div className="storybook-mobile">
        <div className="storybook-mobile-content">
          {renderPage(currentPage, displayPageNumber, displayTotalPages, "mobile")}
        </div>
        <button
          type="button"
          onClick={() => handleMobileNavigation(currentPageIndex - 1)}
          disabled={currentPageIndex === 0 || isPlaying}
          className="storybook-nav-btn storybook-nav-btn--mobile-left"
          aria-label="Previous page"
        >
          <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        {currentPage?.type !== "end" && (
          <button
            type="button"
            onClick={() => handleMobileNavigation(currentPageIndex + 1)}
            disabled={currentPageIndex >= pages.length - 1 || isPlaying}
            className="storybook-nav-btn storybook-nav-btn--mobile-right"
            aria-label="Next page"
          >
            <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}
      </div>
      )}

      {/* DESKTOP */}
      {isDesktopViewport && (
      <div className="storybook-stage">
        <div className={`storybook-book-shell ${isCoverPage ? "storybook-book-shell--cover" : ""}`}>
          {(!isCoverPage || currentPage?.type === "end") && (
            <PageArrow
              direction="left"
              onClick={() => handlePageNavigation(currentPageIndex - 1, "backward")}
              disabled={currentPageIndex === 0 || isFlipping}
              isPlaying={isPlaying}
              extraClass={isCoverPage ? "storybook-nav-btn--cover-left" : ""}
            />
          )}

          <div className={`storybook-book ${useSpreadLayout ? "storybook-book--spread" : "storybook-book--cover"}`}>
            {/* When flipping into the end page the blue hardcover backdrop
                would flash on both sides of the narrow end sheet — fade it
                out as the spread closes. */}
            {useSpreadLayout && <HardcoverFrame fading={isFlipping && !!nextPage && isTargetCoverPage} />}

            <div className={`storybook-pages-wrap ${isCoverPage && !isFlipping ? "storybook-pages-wrap--cover" : "storybook-pages-wrap--spread"}`}>
              {isCoverPage ? (
                <>
                  {isFlipping && nextPage && !isTargetCoverPage && (
                    <div className="storybook-spread-layer storybook-spread-layer--under">
                      {renderSpread(
                        renderPage(nextPage, getPageNumber(targetPageIndex), displayTotalPages, "left"),
                        renderPage(nextPage, getPageNumber(targetPageIndex), displayTotalPages, "right")
                      )}
                    </div>
                  )}
                  <div
                    className={`storybook-cover-sheet ${isFlipping && flipDirection === "forward" ? "flip-sheet-forward" : ""} ${isFlipping && flipDirection === "backward" ? (currentPage?.type === "end" ? "flip-sheet-backward-end" : "flip-sheet-backward") : ""}`}
                    style={coverPageShell}
                  >
                    {renderPage(currentPage, displayPageNumber, displayTotalPages, "cover")}
                  </div>
                </>
              ) : (
                <>
                  {isFlipping && nextPage && !isTargetCoverPage && (
                    <div className="storybook-spread-layer storybook-spread-layer--under">
                      {renderSpread(
                        renderPage(nextPage, getPageNumber(targetPageIndex), displayTotalPages, "left"),
                        renderPage(nextPage, getPageNumber(targetPageIndex), displayTotalPages, "right")
                      )}
                    </div>
                  )}
                  {isFlipping && nextPage && isTargetCoverPage && (
                    <div className="storybook-spread-layer storybook-spread-layer--under">
                      <div style={{ ...coverPageShell, maxWidth: 520, margin: "0 auto" }}>
                        {renderPage(nextPage, getPageNumber(targetPageIndex), displayTotalPages, "cover")}
                      </div>
                    </div>
                  )}
                  {renderSpread(
                    isFlipping && flipDirection === "forward" && nextPage && !isTargetCoverPage
                      ? renderPage(nextPage, getPageNumber(targetPageIndex), displayTotalPages, "left")
                      : isFlipping && flipDirection === "backward" && nextPage && !isTargetCoverPage
                        ? renderPage(nextPage, getPageNumber(targetPageIndex), displayTotalPages, "left")
                        : renderPage(currentPage, displayPageNumber, displayTotalPages, "left"),
                    isFlipping && flipDirection === "backward" && nextPage && !isTargetCoverPage
                      ? renderPage(nextPage, getPageNumber(targetPageIndex), displayTotalPages, "right")
                      : renderPage(currentPage, displayPageNumber, displayTotalPages, "right"),
                    {
                      // Flipping into the end page: the whole spread closes like a
                      // book cover over the single end sheet waiting underneath —
                      // rendering the end page as a spread half flashes two pages.
                      spread:
                        isFlipping && isTargetCoverPage
                          ? flipDirection === "backward"
                            ? "closing-book-spread"
                            : "closing-book-spread--forward"
                          : "",
                      left:
                        (isFlipping && flipDirection === "forward" && !isTargetCoverPage ? "page-flip-left" : "") +
                        (isFlipping && flipDirection === "backward" && !isTargetCoverPage ? " page-flip-left-reverse" : ""),
                      right:
                        (isFlipping && flipDirection === "forward" && !isTargetCoverPage ? "page-flip-right" : "") +
                        (isFlipping && flipDirection === "backward" && !isTargetCoverPage ? " page-flip-right-reverse" : ""),
                    }
                  )}
                </>
              )}
            </div>
          </div>
          {currentPage?.type !== "end" && !(isFlipping && nextPage?.type === "end") && (
            <PageArrow
              direction="right"
              onClick={() => handlePageNavigation(currentPageIndex + 1, "forward")}
              disabled={currentPageIndex >= pages.length - 1 || isFlipping}
              isPlaying={isPlaying}
              extraClass={isCoverPage ? "storybook-nav-btn--cover-right" : ""}
            />
          )}
        </div>
      </div>
      )}
    </div>
  );
};

export default AudiobookFlipBook;
